import React, { useState, useEffect } from "react";
import {
  Card,
  Avatar,
  Typography,
  Space,
  Button,
  Row,
  Col,
  theme,
  Upload,
  Drawer,
  Form,
  Input,
  message,
  Dropdown,
  Popconfirm,
  Modal,
  Divider,
  Empty,
} from "antd";
import {
  FileTextOutlined,
  DeleteOutlined,
  PlusCircleOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
  MoreOutlined,
  EditOutlined,
  UserOutlined,
} from "@ant-design/icons";
import FormType from "features/Enums/FormType";
import AttachmentComponent from "CommonComponents/Attachments/Attachments";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  addLeadNoteAPI,
  editLeadNoteAPI,
  deleteLeadNoteAPI,
  getLeadNoteByIdAPI,
  getLeadNotessListAPI,
  deleteLeadNoteByIdAPI,
} from "../LeadsAPI";
import dayjs from "dayjs";
import { getEmployeeAPI } from "CommonComponents/Lookup/LookupAPI";
import { FormattedMessage, useIntl } from "react-intl";
import InfiniteScroll from "react-infinite-scroller";

function NotesTab() {
  const { token } = theme.useToken();
  const { Text, Title } = Typography;
  const { TextArea } = Input;
  const intl = useIntl();
  const { leadId } = useParams();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [currentNote, setCurrentNote] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const leadNotesList = useSelector((state) => state.leads.leadNoteList);
  const employeeList = useSelector((state) => state.lookup.employesList);
  const leadNoteById = useSelector((state) => state.leads.leadNoteById);
  const formTypeId = Object.values(FormType).find(
    (f) => f.name === "LeadNotes"
  )?.id;
  useEffect(() => {
    if (leadId) {
      dispatch(getEmployeeAPI());
      setPage(1);
      setHasMore(true);
      // loadMoreData(1);
    }
  }, [leadId]);
  const values = {
    pageNo: page,
    pageSize: 10,
    searchQuery: "",
  };
  const loadMoreData = (explicitPage) => {
    const currentPage = explicitPage || page;
    if (loading || !hasMore) return;
    setLoading(true);

    dispatch(getLeadNotessListAPI(leadId, values))
      .then((res) => {
        const items = res?.payload?.items || [];
        const totalPages = res?.payload?.totalPages;
        const currentPage = res?.payload?.pageNumber;

        if (currentPage >= totalPages || items.length === 0) {
          setHasMore(false);
        } else {
          setPage((prev) => prev + 1);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    dispatch(getLeadNoteByIdAPI(leadNoteById?.id));
  }, [leadNoteById, dispatch]);
  const getEmployeeNameById = (employeeId) => {
    const employee = employeeList?.find((item) => item?.id === employeeId);
    return employee
      ? `${employee.firstName} ${employee.lastName}`
      : "Unknown User";
  };
  const showDrawer = (note = null) => {
    setCurrentNote(note);
    if (note) {
      form.setFieldsValue({
        title: note.title,
        note: note.note,
      });
    } else {
      form.resetFields();
    }
    setOpen(true);
  };
  const onClose = () => {
    setOpen(false);
    //dispatch(getLeadNotessListAPI(leadId));
  };
  const handleSubmit = async (values) => {
    const noteData = {
      id: currentNote?.id || 0,
      leadId: leadId,
      title: values.title,
      note: values.note,
    };
    setLoading(true);
    try {
      if (currentNote) {
        await dispatch(editLeadNoteAPI(noteData, leadId, values)).then(() => {
          setOpen(false);
          form.resetFields();
        });
      } else {
        await dispatch(addLeadNoteAPI(noteData, leadId, values)).then(() => {
          setOpen(false);
          form.resetFields();
        });
      }
    } catch (error) {
      //message.error("Error saving note: " + (error.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };
  const onCancel = () => {
    if (form.isFieldsTouched()) {
      Modal.confirm({
        title: intl.formatMessage({ id: "LeadNote.CancelConfirmation" }),
        okText: intl.formatMessage({ id: "Common.Yes" }),
        cancelText: intl.formatMessage({ id: "Common.No" }),
        onOk: () => {
          form.resetFields();
          onClose();
        },
      });
    } else {
      onClose();
    }
  };

  const handleDelete = (noteId) => {
    Modal.confirm({
      title: intl.formatMessage({ id: "LeadNote.DeleteConfirmation" }),
      okText: intl.formatMessage({ id: "Common.Yes" }),
      okType: "danger",
      cancelText: intl.formatMessage({ id: "Common.No" }),
      onOk() {
        dispatch(deleteLeadNoteByIdAPI(noteId, leadId, values));
      },
    });
  };

  const items = (note) => [
    {
      key: "edit",
      label: (
        <span>
          <EditOutlined />
          &nbsp;&nbsp;
          <FormattedMessage id="Common.Edit" />
        </span>
      ),
      onClick: () => showDrawer(note),
    },
    {
      key: "delete",
      label: (
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handleDelete(note.id);
          }}
        >
          <DeleteOutlined />
          &nbsp;&nbsp;
          <FormattedMessage id="Common.Delete" />
        </a>
      ),
    },
  ];

  return (
    <>
      <div size="small" className="notes-btn">
        <Button
          color="primary"
          variant="dashed"
          block
          className="mb-10 new-btn-add"
          onClick={() => showDrawer()}
          disabled={loading}
        >
          <PlusCircleOutlined />
          {<FormattedMessage id="LeadCall.AddNew" />}
        </Button>
        <div style={{ maxHeight: "60vh", overflow: "auto" }}>
          <InfiniteScroll
            pageStart={1}
            loadMore={loadMoreData}
            hasMore={!loading && hasMore}
            useWindow={false}
            threshold={100}
            loader={
              <div
                key="loader"
                style={{ textAlign: "center", padding: "1rem" }}
              >
                {<FormattedMessage id="Common.loading" />}
              </div>
            }
          >
            <Space direction="vertical" size={10} style={{ width: "100%" }}>
              {leadNotesList?.length > 0 ? (
                leadNotesList.map((note) => (
                  <Card key={note.id} className="activitytab">
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: "16px",
                        width: "100%",
                      }}
                    >
                      {/* Left Section */}
                      <Space align="start">
                        <Avatar
                          shape="square"
                          size={32}
                          icon={
                            <FileTextOutlined style={{ color: "#ffa500" }} />
                          }
                          style={{
                            background: "#fff6e6",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        />
                        <div>
                          <Text strong>{note.title}</Text>
                          <p>{note.note}</p>
                          <AttachmentComponent
                            identifier={formTypeId}
                            currentId={note?.id}
                            isPreview={true}
                            getattachments={true}
                          />
                          <Text
                            type="secondary"
                            strong
                            className="username-new mt-10 f-11"
                          >
                            Updated by {getEmployeeNameById(note.createdBy)}
                            ,&nbsp;&nbsp;&nbsp;
                            {dayjs(note.createdDate).format(
                              "DD MMM YYYY, hh:mm a"
                            )}
                          </Text>
                        </div>
                      </Space>

                      {/* Right Section */}
                      <div className="activitytab-dropdown text-right">
                        <Dropdown
                          menu={{ items: items(note) }}
                          trigger={["click"]}
                          disabled={loading}
                          className="mt-5px"
                        >
                          <Button onClick={(e) => e.preventDefault()}>
                            <MoreOutlined />
                          </Button>
                        </Dropdown>
                      </div>
                    </div>
                  </Card>
                ))
              ) : (
                <Card className="text-center">
                  <Empty description={false} />
                  <Text type="secondary">
                    {<FormattedMessage id="NoData" />}
                  </Text>
                </Card>
              )}

              {!hasMore && leadNotesList?.length > 0 && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "0",
                    color: "#999",
                  }}
                >
                  {<FormattedMessage id="Common.EndOfList" />}
                </div>
              )}
            </Space>
          </InfiniteScroll>
        </div>
      </div>

      <Drawer
        title={
          currentNote ? (
            <FormattedMessage id="LeadNote.EditLeadNote" />
          ) : (
            <FormattedMessage id="LeadNote.AddLeadNote" />
          )
        }
        onClose={onClose}
        open={open}
        width="500px"
      >
        <Form
          form={form}
          name="note-form"
          layout="vertical"
          autoComplete="off"
          size="large"
          onFinish={handleSubmit}
        >
          <Form.Item
            label={<FormattedMessage id="LeadNote.Title" />}
            name="title"
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: "LeadNote.Error.EnterTitle",
                }),
              },
            ]}
          >
            <Input
              placeholder={intl.formatMessage({
                id: "LeadNote.Placeholder.EnterTitle",
              })}
              maxLength={100}
            />
          </Form.Item>
          <Form.Item
            label={<FormattedMessage id="LeadNote.Note" />}
            name="note"
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: "LeadNote.Error.EnterNotes",
                }),
              },
            ]}
          >
            <TextArea
              rows={4}
              placeholder={intl.formatMessage({
                id: "LeadNote.Placeholder.EnterNotes",
              })}
              maxLength={255}
              showCount
            />
          </Form.Item>
          <Form.Item
            label={<FormattedMessage id="Common.Attachment" />}
            name="attachment"
          >
            <AttachmentComponent
              identifier={formTypeId}
              currentId={currentNote ? currentNote?.id : null}
              getattachments={true}
            />
          </Form.Item>
          <Row>
            <Col span={24} className="text-center mt-10">
              <Space>
                <Button
                  danger
                  variant="outlined"
                  size="medium"
                  onClick={onCancel}
                  disabled={loading}
                >
                  <CloseCircleOutlined />{" "}
                  {<FormattedMessage id="Common.Cancel" />}
                </Button>
                <Button
                  type="primary"
                  size="medium"
                  htmlType="submit"
                  loading={loading}
                >
                  <CheckCircleOutlined />
                  {<FormattedMessage id="Common.Save" />}
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Drawer>
    </>
  );
}

export default NotesTab;
